import { useState } from 'react'
import { Alert } from 'reactstrap'

import { SOURCE, DESTINATION, hasTimeCollision } from './incidentMergeFields'
import './Modals.css'

// Step 1 of the Transfer Incident Data modal: pick which incident's value the surviving
// (destination) incident keeps, one row per column where the two disagree.

const EMPTY = <span className="merge-field-value-empty">— empty</span>

const ValueCard = ({ display, origin, selected, long, expanded, onToggleExpand, onSelect }) => (
    <div className="merge-field-card-wrapper">
        <button
            type="button"
            className={`merge-field-card${selected ? " merge-field-card-selected" : ""}`}
            onClick={onSelect}
            aria-pressed={selected}
        >
            <span className={`merge-field-value${long && !expanded ? " merge-field-value-long" : ""}`}>
                {selected && <i className="fa fa-check mr-1" aria-hidden="true" />}
                {display || EMPTY}
            </span>
            <span className="merge-field-origin">{origin}</span>
        </button>
        {long && display && (
            <button type="button" className="merge-field-expand-toggle" onClick={onToggleExpand}>
                {expanded ? "Show less" : "Show more"}
            </button>
        )}
    </div>
)

const IncidentFieldMergeStep = ({ from, to, differing, identical, resolutions, onResolve }) => {
    const [showIdentical, setShowIdentical] = useState(false)
    const [expandedRows, setExpandedRows] = useState({})

    const fromOrigin = `from #${from?.pk || "--"}`
    const toOrigin = `from #${to?.pk || "--"} (dest)`
    const timeCollision = hasTimeCollision(from, to, resolutions)

    return (
        <>
            <Alert color="info" className="py-2">
                <i className="fa fa-info-circle mr-1" aria-hidden="true" />
                Pick which value <strong>{to?.title || "Untitled incident"} (ID #{to?.pk || "--"})</strong> keeps.
                Anything you leave alone stays as it is on that incident.
            </Alert>

            {differing.length === 0 ? (
                <p className="text-muted small">All comparable fields match on both incidents.</p>
            ) : (
                differing.map((row) => {
                    const selected = resolutions[row.key] || DESTINATION
                    const expanded = !!expandedRows[row.key]
                    const toggleExpand = () =>
                        setExpandedRows((prev) => ({ ...prev, [row.key]: !prev[row.key] }))

                    return (
                        <div className="merge-field-row" key={row.key}>
                            <div className="merge-field-label">{row.label}</div>
                            <div className="merge-field-options">
                                <ValueCard
                                    display={row.fromDisplay}
                                    origin={fromOrigin}
                                    selected={selected === SOURCE}
                                    long={row.long}
                                    expanded={expanded}
                                    onToggleExpand={toggleExpand}
                                    onSelect={() => onResolve(row.key, SOURCE)}
                                />
                                <ValueCard
                                    display={row.toDisplay}
                                    origin={toOrigin}
                                    selected={selected === DESTINATION}
                                    long={row.long}
                                    expanded={expanded}
                                    onToggleExpand={toggleExpand}
                                    onSelect={() => onResolve(row.key, DESTINATION)}
                                />
                            </div>
                            {row.key === "incident_time" && timeCollision && (
                                <p className="merge-field-warning">
                                    <i className="fa fa-exclamation-triangle mr-1" aria-hidden="true" />
                                    Both incidents will then share the same car, date, and time. Archive
                                    incident #{from?.pk || "--"} after the transfer.
                                </p>
                            )}
                        </div>
                    )
                })
            )}

            {identical.length > 0 && (
                <div className="mt-3">
                    <button
                        type="button"
                        className="merge-identical-toggle"
                        onClick={() => setShowIdentical((prev) => !prev)}
                    >
                        <i
                            className={`fa fa-chevron-${showIdentical ? "up" : "down"} mr-1`}
                            aria-hidden="true"
                        />
                        {identical.length} identical field{identical.length === 1 ? "" : "s"}
                    </button>
                    {showIdentical && (
                        <ul className="merge-identical-list">
                            {identical.map((row) => (
                                <li key={row.key}>
                                    {row.label}: {row.toDisplay || EMPTY}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </>
    )
}

export default IncidentFieldMergeStep
